import { differenceInYears, isValid } from 'date-fns';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { AccountOnboardingView, GlobalAccount, GUID, PersonOrgType, UnencryptedData } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatDate, formatPhoneNumber, getAccountOwnerName, getHouseholdName } from '@/utils';
import { HouseholdLogoCard, PersonLogoCard } from '@/widgets';
import { useCommandCenterHouseholds, useHouseholdMap } from 'views/household/household-queries';
import { PotentialDuplicateDialog } from 'views/person/PotentialDuplicateDialog';
import { usePotentialPersonDuplicates } from 'views/person/usePotentialPersonDuplicates';

import {
    checkAccountOwnerDuplicates,
    useAssignAccountToHousehold,
    useCreateHouseholdAndAssignAccount,
} from './account-onboarding-queries';

interface AccountOnboardingSheetProps {
    accountOnboardingView: AccountOnboardingView;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

interface AccountOnboardingFormData {
    householdGUID: { value: string; label: string };
    newHouseholdName: string;
    primaryAccountManager: { value: string; label: string } | undefined;
    [key: string]: unknown;
}

export const AccountOnboardingSheet = (props: AccountOnboardingSheetProps) => {
    const { data: households } = useCommandCenterHouseholds();
    const householdMap = useHouseholdMap();
    const { mutate: assignAccount, isPending } = useAssignAccountToHousehold();
    const { mutate: createHousehold, isPending: createPending } = useCreateHouseholdAndAssignAccount();
    const navigate = useNavigate();

    const totalSteps = 2 + props.accountOnboardingView.accountOwners.length;
    const [currentStep, setCurrentStep] = useState(0);
    const [isCreatingNewHousehold, setIsCreatingNewHousehold] = useState(false);

    const defaultHousehold = householdMap.get(props.accountOnboardingView.householdGUID);
    const defaultHouseholdOption = defaultHousehold
        ? {
              value: defaultHousehold.globalAccountGUID,
              label: getHouseholdName(defaultHousehold),
          }
        : { value: '', label: '' };
    const householdOptions: ComboboxOption[] =
        households
            ?.map((household) => ({
                value: household.globalAccountGUID,
                label: getHouseholdName(household) || 'Unnamed Household',
            }))
            .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const form = useForm<AccountOnboardingFormData>({
        defaultValues: {
            householdGUID: defaultHouseholdOption,
            newHouseholdName: '',
            primaryAccountManager: undefined,
        },
        mode: 'onChange',
    });

    const selectedHouseholdOption = form.watch('householdGUID');
    const selectedHouseholdGUID = selectedHouseholdOption?.value || '';
    const duplicates = usePotentialPersonDuplicates(
        JSON.stringify([
            props.open,
            props.accountOnboardingView.accountNumber,
            props.accountOnboardingView.tenantGUID,
            selectedHouseholdGUID,
            isCreatingNewHousehold,
        ]),
    );
    const newHouseholdName = form.watch('newHouseholdName') || '';
    const primaryAccountManager = form.watch('primaryAccountManager') as { value: string; label: string } | undefined;
    const eligibleOwners: ComboboxOption[] = props.accountOnboardingView.accountOwners
        .filter((owner) => owner.taxIdHash && owner.personOrgType === PersonOrgType.PERSON)
        .map((owner) => ({
            value: `${owner.taxIdHash}|${owner.personOrgType}`,
            label: getAccountOwnerName(owner),
        }));

    const handleToggleCreateNew = () => {
        if (isCreatingNewHousehold) {
            setIsCreatingNewHousehold(false);
            form.setValue('newHouseholdName', '');
            form.setValue('primaryAccountManager', undefined);
        } else {
            setIsCreatingNewHousehold(true);
            form.setValue('householdGUID', { value: '', label: '' });
        }
    };

    useEffect(() => {
        if (selectedHouseholdGUID) {
            setIsCreatingNewHousehold(false);
            form.setValue('newHouseholdName', '');
            form.setValue('primaryAccountManager', undefined);
        }
    }, [selectedHouseholdGUID]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = () => {
        if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onboard = (data: AccountOnboardingFormData) => {
        if (isCreatingNewHousehold) {
            const ownerKey = data.primaryAccountManager?.value;
            if (!ownerKey) return;
            createHousehold(
                {
                    tenantGUID: props.accountOnboardingView.tenantGUID,
                    accountNumber: props.accountOnboardingView.accountNumber,
                    householdName: data.newHouseholdName,
                    primaryAccountManagerOwnerKey: ownerKey,
                },
                {
                    onSuccess: (householdGUID) => {
                        props.onOpenChange(false);
                        navigate(`/household/${householdGUID}`);
                    },
                },
            );
        } else {
            const householdGUID = data.householdGUID.value;
            if (!householdGUID) return;
            assignAccount(
                {
                    accountNumber: props.accountOnboardingView.accountNumber,
                    householdGUID,
                },
                {
                    onSuccess: () => {
                        props.onOpenChange(false);
                        navigate(`/household/${householdGUID}`);
                    },
                },
            );
        }
    };

    const handleSubmit = (data: AccountOnboardingFormData) => {
        void duplicates.check(
            () => checkAccountOwnerDuplicates(props.accountOnboardingView),
            () => onboard(data),
        );
    };

    const isNextDisabled =
        currentStep === 0 && (isCreatingNewHousehold ? !newHouseholdName.trim() : !selectedHouseholdGUID);
    const isLastStep = currentStep === totalSteps - 1;
    const selectedHousehold = householdMap.get(selectedHouseholdGUID);

    return (
        <Sheet open={props.open} onOpenChange={props.onOpenChange}>
            <SheetContent side="right" className="flex w-[400px] flex-col sm:w-[540px]" aria-describedby={undefined}>
                <SheetHeader className="flex-shrink-0">
                    <SheetTitle>Link Account to Household</SheetTitle>
                    <SheetDescription>
                        Select a Household to link account{' '}
                        <span className="font-bold">{props.accountOnboardingView.accountNumber}</span>
                    </SheetDescription>
                    <SheetDescription className="pt-2 font-medium">
                        Step {currentStep + 1} of {totalSteps}
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-6">
                    <FormProvider {...form}>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                                {currentStep === 0 && (
                                    <div className="space-y-6">
                                        <div className="border-b pb-2">
                                            <h3 className="text-lg font-semibold">Select Household</h3>
                                        </div>
                                        {props.accountOnboardingView.householdGUID && (
                                            <div className="flex flex-col gap-4">
                                                <h2 className="font-medium">Suggested Household</h2>
                                                <div className="flex justify-between gap-2 pl-2">
                                                    <HouseholdCell
                                                        householdGUID={props.accountOnboardingView.householdGUID}
                                                    />
                                                    <Button
                                                        type="button"
                                                        disabled={
                                                            !defaultHousehold?.globalAccountGUID ||
                                                            selectedHouseholdGUID ===
                                                                props.accountOnboardingView.householdGUID
                                                        }
                                                        onClick={() => {
                                                            form.setValue('householdGUID', defaultHouseholdOption);
                                                        }}
                                                        variant="ghost"
                                                    >
                                                        Select
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <label className="text-sm leading-none font-medium">
                                                Select Household *
                                            </label>
                                            <div className="flex gap-2">
                                                <div className="flex-1">
                                                    <FormCombobox
                                                        formMethods={form}
                                                        name="householdGUID"
                                                        options={householdOptions satisfies ComboboxOption[]}
                                                        selectOptionLabel="Select a household"
                                                        noOptionsLabel="No households found"
                                                        placeholder="Select a household"
                                                        disabled={isCreatingNewHousehold}
                                                        required={!isCreatingNewHousehold}
                                                    />
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant={isCreatingNewHousehold ? 'secondary' : 'outline'}
                                                    size="icon"
                                                    className="shrink-0"
                                                    onClick={handleToggleCreateNew}
                                                    title={
                                                        isCreatingNewHousehold
                                                            ? 'Cancel creating new household'
                                                            : 'Create new household'
                                                    }
                                                >
                                                    {isCreatingNewHousehold ? '×' : '+'}
                                                </Button>
                                            </div>
                                        </div>
                                        {isCreatingNewHousehold && (
                                            <FormField
                                                control={form.control}
                                                name="newHouseholdName"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Household Name *</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="Enter household name" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        )}
                                    </div>
                                )}

                                {currentStep > 0 && currentStep <= props.accountOnboardingView.accountOwners.length && (
                                    <OwnerStep owner={props.accountOnboardingView.accountOwners[currentStep - 1]} />
                                )}

                                {isLastStep && (
                                    <div className="space-y-6">
                                        <SummaryStep
                                            accountNumber={props.accountOnboardingView.accountNumber}
                                            household={selectedHousehold}
                                            accountOwners={props.accountOnboardingView.accountOwners}
                                            isCreatingNewHousehold={isCreatingNewHousehold}
                                            newHouseholdName={newHouseholdName}
                                        />
                                        {isCreatingNewHousehold && (
                                            <div className="space-y-4">
                                                <div className="border-b pb-2">
                                                    <h3 className="text-base font-semibold">Primary Account Manager</h3>
                                                </div>
                                                <FormCombobox
                                                    formMethods={form}
                                                    name="primaryAccountManager"
                                                    label="Select Primary Account Manager *"
                                                    options={eligibleOwners}
                                                    placeholder="Select account manager"
                                                    noOptionsLabel="No eligible account owners"
                                                    required
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </form>
                        </Form>
                    </FormProvider>
                </div>

                <SheetFooter className="flex flex-shrink-0 gap-2 border-t pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleBack}
                        disabled={isPending || createPending || duplicates.busy || currentStep === 0}
                        className="flex-1"
                    >
                        Back
                    </Button>
                    {!isLastStep ? (
                        <Button type="button" onClick={handleNext} disabled={isNextDisabled} className="flex-1">
                            Next
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            onClick={form.handleSubmit(handleSubmit)}
                            className="flex-1"
                            disabled={
                                isPending ||
                                createPending ||
                                duplicates.busy ||
                                (isCreatingNewHousehold && !primaryAccountManager?.value)
                            }
                        >
                            {isPending || createPending
                                ? isCreatingNewHousehold
                                    ? 'Creating...'
                                    : 'Assigning...'
                                : isCreatingNewHousehold
                                  ? 'Create Household'
                                  : 'Assign to Household'}
                        </Button>
                    )}
                </SheetFooter>
            </SheetContent>
            <PotentialDuplicateDialog
                result={duplicates.result}
                onBack={duplicates.cancel}
                onProceed={duplicates.proceed}
                confirmText={isCreatingNewHousehold ? 'Create anyway' : 'Assign anyway'}
            />
        </Sheet>
    );
};

const OwnerStep = ({ owner }: { owner: AccountOnboardingView['accountOwners'][0] }) => {
    return (
        <div className="space-y-6">
            <div className="border-b pb-2">
                <h3 className="text-lg font-semibold">Review Account Owner</h3>
            </div>
            <PersonDetails
                name={getAccountOwnerName(owner)}
                dateOfBirth={owner.dateOfBirth?.plainText}
                taxId={owner.taxId?.plainText}
            />
            <ContactInfo email={owner.primaryEmail} phone={owner.primaryPhone} workPhone={owner.workPhone} />
            <AddressSection
                title="Primary Address"
                street={owner.primaryStreet}
                street2={owner.primaryStreet2}
                city={owner.primaryCity}
                state={owner.primaryState}
                zip={owner.primaryZip}
            />
            <AddressSection
                title="Mailing Address"
                street={owner.mailingStreet}
                street2={owner.mailingStreet2}
                city={owner.mailingCity}
                state={owner.mailingState}
                zip={owner.mailingZip}
            />
        </div>
    );
};

const PersonDetails = ({ name, dateOfBirth, taxId }: { name: string; dateOfBirth?: string | null; taxId?: string }) => {
    const age =
        dateOfBirth && isValid(new Date(dateOfBirth)) ? differenceInYears(new Date(), new Date(dateOfBirth)) : null;

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-700">Person Details</h4>
            <div className="grid grid-cols-3 gap-y-3 text-sm">
                <span className="col-span-1 text-gray-500">Name:</span>
                <span className="text-cocoa-1000 col-span-2">{name || '—'}</span>
                {dateOfBirth && (
                    <>
                        <span className="col-span-1 text-gray-500">Date of Birth:</span>
                        <span className="text-cocoa-1000 col-span-2">
                            {formatDate(dateOfBirth)}
                            {age !== null && ` (Age: ${age})`}
                        </span>
                    </>
                )}
                {taxId && (
                    <>
                        <span className="col-span-1 text-gray-500">Tax ID:</span>
                        <span className="text-cocoa-1000 col-span-2">{taxId}</span>
                    </>
                )}
            </div>
        </div>
    );
};

const ContactInfo = ({
    email,
    phone,
    workPhone,
}: {
    email?: string | null;
    phone?: string | null;
    workPhone?: string | null;
}) => {
    if (!email && !phone && !workPhone) return null;

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-700">Contact Information</h4>
            <div className="grid grid-cols-3 gap-y-3 text-sm">
                {email && (
                    <>
                        <span className="col-span-1 text-gray-500">Email:</span>
                        <span className="text-cocoa-1000 col-span-2">{email}</span>
                    </>
                )}
                {phone && (
                    <>
                        <span className="col-span-1 text-gray-500">Phone:</span>
                        <span className="text-cocoa-1000 col-span-2">{formatPhoneNumber(phone) || phone}</span>
                    </>
                )}
                {workPhone && (
                    <>
                        <span className="col-span-1 text-gray-500">Work Phone:</span>
                        <span className="text-cocoa-1000 col-span-2">{formatPhoneNumber(workPhone) || workPhone}</span>
                    </>
                )}
            </div>
        </div>
    );
};

const AddressSection = ({
    title,
    street,
    street2,
    city,
    state,
    zip,
}: {
    title: string;
    street?: string | null;
    street2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
}) => {
    if (!street && !city && !state && !zip) return null;

    const addressParts = [street, street2, [city, state, zip].filter(Boolean).join(' ').trim()].filter(Boolean);

    return (
        <div>
            <h4 className="mb-3 text-sm font-semibold text-gray-700">{title}</h4>
            <div className="text-cocoa-1000 text-sm">{addressParts.join(', ')}</div>
        </div>
    );
};

const SummaryStep = ({
    accountNumber,
    household,
    accountOwners,
    isCreatingNewHousehold,
    newHouseholdName,
}: {
    accountNumber: string;
    household: ReturnType<typeof useHouseholdMap> extends Map<string, infer V> ? V | undefined : never;
    accountOwners: AccountOnboardingView['accountOwners'];
    isCreatingNewHousehold?: boolean;
    newHouseholdName?: string;
}) => {
    const toUnencryptedData = (value?: string): UnencryptedData | undefined => {
        return value ? ({ plainText: value } as UnencryptedData) : undefined;
    };

    return (
        <div className="space-y-4">
            <div className="border-b pb-2">
                <h3 className="text-lg font-semibold">Summary</h3>
            </div>

            <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Account Number</h4>
                <p className="text-cocoa-1000 font-mono text-sm">{accountNumber}</p>
            </div>

            <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">
                    {isCreatingNewHousehold ? 'New Household' : 'Selected Household'}
                </h4>
                {isCreatingNewHousehold ? (
                    <p className="text-cocoa-1000 text-sm font-medium">{newHouseholdName || '—'}</p>
                ) : household ? (
                    <div className="pt-3 pl-2">
                        <HouseholdLogoCard
                            name={getHouseholdName(household)}
                            to={`/household/${household.globalAccountGUID}`}
                        />
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">No household selected</p>
                )}
            </div>

            <div>
                <h4 className="mb-3 text-sm font-medium text-gray-700">Account Owners</h4>
                <div className="space-y-3 pl-2">
                    {accountOwners.map((owner, index) => (
                        <PersonLogoCard
                            key={index}
                            personInfo={{
                                firstName: toUnencryptedData(owner?.firstName),
                                lastName: toUnencryptedData(owner?.lastName),
                                orgName: toUnencryptedData(owner?.orgName),
                                primaryEmail: toUnencryptedData(owner?.primaryEmail),
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export const HouseholdCell = (props: { householdGUID: GUID<GlobalAccount> }) => {
    const householdMap = useHouseholdMap();
    const household = householdMap.get(props.householdGUID);

    return household ? (
        <a
            href={`/household/${household.globalAccountGUID}`}
            className="text-cocoa-1000 text-base font-thin hover:underline"
            onClick={(e) => e.stopPropagation()}
        >
            {getHouseholdName(household)}
        </a>
    ) : (
        <span className="text-cocoa-1000 text-base font-thin">—</span>
    );
};
